// Line shaped progress bar
import { I18nThemed } from 'interfaces/mixins';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v } from '@dojo/framework/core/vdom';
import { DNode } from '@dojo/framework/core/interfaces';

import { numeral } from '@holmescorp/numeral';

import css from '../css/circle.m.css';

export interface CircleProperties {
	legend?: string;
	progress: number;
	title?: string;
	inner?: number;
}

const SET_COLOR = 'currentColor';
const UNSET_COLOR = 'rgb(255, 255, 255)';

@theme(css)
export class Circle extends I18nThemed<CircleProperties> {
	protected innerLinearGradient(gradientId: string): DNode {
		const { inner } = this.properties;
		if (inner !== undefined) {
			const s1 = inner > 0 ? SET_COLOR : UNSET_COLOR;
			const s2 = s1;
			const s3 = inner < 99 ? UNSET_COLOR : SET_COLOR;
			const s3offset = Math.min(inner + 10, 100);

			return v('linearGradient', { id: gradientId, x1: '0%', y1: '0%', x2: '100%', y2: '0%' }, [
				v('stop', { key: 's1', offset: '0%', style: `stop-color:${s1};stop-opacity:0.5` }),
				v('stop', { key: 's2', offset: `${inner}%`, style: `stop-color:${s2};stop-opacity:0.5` }),
				v('stop', { key: 's3', offset: `${s3offset}%`, style: `stop-color:${s3};stop-opacity:0.5` })
			]);
		}
		return null;
	}

	protected linearGradient(gradientId: string): DNode {
		const { progress } = this.properties;
		const s1 = progress > 0 ? SET_COLOR : UNSET_COLOR;
		const s2 = s1;
		const s3 = progress < 99 ? UNSET_COLOR : SET_COLOR;
		const s3offset = Math.min(progress + 10, 100);

		return v('linearGradient', { id: gradientId, x1: '0%', y1: '0%', x2: '100%', y2: '0%' }, [
			v('stop', { key: 's1', offset: '0%', style: `stop-color:${s1};stop-opacity:1` }),
			v('stop', { key: 's2', offset: `${progress}%`, style: `stop-color:${s2};stop-opacity:1` }),
			v('stop', { key: 's3', offset: `${s3offset}%`, style: `stop-color:${s3};stop-opacity:1` })
		]);
	}

	protected render(): DNode {
		const { key = 'pbcircle', legend, progress, inner, title } = this.properties;
		const gradientId = `grad1-${key}`;
		const innerId = `gradinner1-${key}`;
		const titleId = `${key}TitleId`;
		const prog = numeral(progress).format('0');
		const innerTitle = inner ? `\nAssessment: ${numeral(inner).format('0')}%` : '';
		const ariaTitle = title ? `${title}: ${prog}%${innerTitle}` : `${prog}%${innerTitle}`;

		return v('div', { key, classes: [this.theme(css.root)] }, [
			v('svg', { viewBox: `0 0 116 110`, role: 'img', 'aria-labelledby': titleId }, [
				v('title', { key: 'pb0', id: titleId }, [ariaTitle]),
				v('defs', { key: 'defs' }, [this.linearGradient(gradientId), this.innerLinearGradient(innerId)]),
				title && v('text', { key: 'pb1', x: '55', classes: [this.theme(css.title)] }, [title]),
				v('circle', {
					classes: [this.theme(css.progressBar)],
					key: 'pb2',
					cx: '58',
					cy: '61',
					r: '40',
					stroke: `url(#${gradientId})`,
					fill: 'transparent'
				}),
				inner !== undefined
					? v('circle', {
							classes: [this.theme(css.innerBar)],
							key: 'pb3',
							cx: '58',
							cy: '61',
							r: '35',
							stroke: `url(#${innerId})`,
							fill: 'transparent'
							// tslint:disable-next-line:indent
					  })
					: null,
				legend && v('text', { key: 'pb4', x: '60', y: '68', classes: [this.theme(css.legend)] }, [legend])
			])
		]);
	}
}

export default Circle;
